/** Hook that gives the picker a unified view of built-in +
 *  user palettes and the user's currently-active palette cursor.
 *
 *  The mutation functions live on the prefs module
 *  (`user-palettes-prefs.ts`); this hook subscribes to its
 *  internal event emitter via `subscribeToUserPalettes` so every
 *  mounted `ColorSwatchPopover` re-renders when ANYONE — another
 *  hook instance, a test, the import flow — mutates the store.
 *
 *  Built-in palettes are always available regardless of storage
 *  state. `activePaletteId` resolves to:
 *
 *    1. The user's stored cursor, if it matches a built-in OR a
 *       user palette that still exists.
 *    2. The first built-in palette (`builtin:material`) otherwise.
 *
 *  This means a freshly-cleared store gracefully shows Material
 *  on first open. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BUILTIN_PALETTES, getBuiltinPalette, type Palette } from './builtin-palettes';
import {
  __resetPalettesInMemoryForTests,
  __resetPalettesListenersForTests,
  appendColorToPalette,
  clearAllUserPalettes,
  createPalette,
  deletePalette,
  loadUserPalettes,
  removeColorFromPalette,
  renamePalette,
  setActivePaletteId,
  subscribeToUserPalettes,
  type UserPalette,
} from './user-palettes-prefs';

export interface UsePalettesResult {
  /** Built-in + user palettes, in display order. */
  allPalettes: Palette[];
  /** The full set of user palettes (subset of `allPalettes`). */
  userPalettes: UserPalette[];
  /** The active palette resolved against built-in + user lists.
   *  Always defined — falls back to the first built-in. */
  activePalette: Palette;
  /** Persist a new active palette cursor. Pass a built-in id
   *  (`builtin:material`) or a user palette id. */
  selectPalette: (id: string) => void;
  /** Create a new user palette and make it active. */
  newPalette: (name: string, colors?: string[]) => void;
  /** Rename a user palette. Built-in renames are silently ignored. */
  rename: (id: string, name: string) => void;
  /** Delete a user palette. Built-in deletes are silently ignored. */
  remove: (id: string) => void;
  /** Append a color to a user palette. No-op on built-in ids. */
  addColor: (id: string, color: string) => void;
  /** Remove a color from a user palette. */
  removeColor: (id: string, color: string) => void;
  /** Wipe ALL user palettes + reset active to the first built-in. */
  resetAllUser: () => void;
}

export function usePalettes(): UsePalettesResult {
  const [state, setState] = useState(() => loadUserPalettes());

  useEffect(() => subscribeToUserPalettes(setState), []);

  const allPalettes = useMemo(
    () => [...BUILTIN_PALETTES, ...state.palettes.map((up) => userPaletteToPalette(up))],
    [state.palettes],
  );

  const activePalette = useMemo(() => {
    if (state.activePaletteId) {
      const found = allPalettes.find((p) => p.id === state.activePaletteId);
      if (found) return found;
    }
    return allPalettes[0] ?? BUILTIN_PALETTES[0]!;
  }, [state.activePaletteId, allPalettes]);

  const selectPalette = useCallback((id: string) => {
    setActivePaletteId(id);
  }, []);

  const newPalette = useCallback((name: string, colors?: string[]) => {
    createPalette(name, colors);
  }, []);

  const rename = useCallback((id: string, name: string) => {
    if (id.startsWith('builtin:')) return;
    renamePalette(id, name);
  }, []);

  const remove = useCallback((id: string) => {
    if (id.startsWith('builtin:')) return;
    deletePalette(id);
  }, []);

  const addColor = useCallback((id: string, color: string) => {
    if (id.startsWith('builtin:')) return;
    appendColorToPalette(id, color);
  }, []);

  const removeColor = useCallback((id: string, color: string) => {
    if (id.startsWith('builtin:')) return;
    removeColorFromPalette(id, color);
  }, []);

  const resetAllUser = useCallback(() => {
    clearAllUserPalettes();
  }, []);

  return {
    allPalettes,
    userPalettes: state.palettes,
    activePalette,
    selectPalette,
    newPalette,
    rename,
    remove,
    addColor,
    removeColor,
    resetAllUser,
  };
}

function userPaletteToPalette(up: UserPalette): Palette {
  return {
    id: up.id,
    name: up.name,
    kind: 'user',
    groups: [{ label: '', colors: up.colors }],
  };
}

/** Resolve a palette id to its concrete `Palette`. Returns
 *  `undefined` if the id is unknown — useful for the import flow
 *  before the user has a state-syncing hook on the page. */
export function resolvePalette(id: string): Palette | undefined {
  if (id.startsWith('builtin:')) return getBuiltinPalette(id);
  const stored = loadUserPalettes();
  const up = stored.palettes.find((p) => p.id === id);
  return up ? userPaletteToPalette(up) : undefined;
}

/** Test-only reset. Order matters: clear storage + emit empty
 *  FIRST so any currently-mounted subscribers see `{ palettes:
 *  [], activePaletteId: null }`, THEN drop the listener set so
 *  surviving setters from a not-yet-cleaned prior render can't
 *  receive any further emits. */
export function __resetPalettesForTests(): void {
  clearAllUserPalettes();
  __resetPalettesInMemoryForTests();
  __resetPalettesListenersForTests();
}
